import { useCallback } from 'react';
import type { AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import type { GetWorkspaceTranscript } from '@trinity/chat/api/interfaces/workspace.transcript';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { type UseQueryOptions, useQueryWrapper } from '@trinity/chat-components/hooks/useQueryWrapper';

type Data = GetWorkspaceTranscript.Response;
type QueryKey = readonly [string, ITranscriptId];

const buildQueryKey = (data: ITranscriptId): QueryKey => {
  return ['workspace-transcript', data];
};

export function useWorkspaceTranscriptQuery(
  data: ITranscriptId,
  options?: Omit<UseQueryOptions<Data, AxiosResponse, Data, QueryKey>, 'queryKey' | 'queryFn'>,
) {

  const apiClient = useChatApiClient();

  return useQueryWrapper({
    queryKey: buildQueryKey(data),
    queryFn: () => {
      return apiClient.workspacesTranscript.getWorkspaceTranscript({
        transcriptId: data.transcriptId,
      });
    },
    ...options,
  });
}

export function useInvalidateWorkspaceTranscriptQuery() {
  const queryClient = useQueryClient();

  return useCallback((data: ITranscriptId) => {
    const queryKey = buildQueryKey(data);

    return queryClient.invalidateQueries({ queryKey });
  }, [queryClient]);
}